import { reactive, type InjectionKey } from 'vue'
import { BookingState, BookingStep, Direction } from '../../shared/types/enums'
import { generateSessionId } from '../../shared/rules'
import type {
  AvailabilitiesByDirection,
  Booking,
  Brand,
  Journey,
  Place,
  SearchState,
  Tariff,
  VehicleModel,
} from '../../shared/types'
import { injectStrict } from './utils/inject'
import type { WidgetMode } from './utils/token'

/** Paire de slots aller/retour indexée par sous-groupe passagers (1 / 2). */
export interface GroupSlot<T> {
  group1: T
  group2: T
}

export interface SelectedTariffs {
  outward: GroupSlot<Tariff | null>
  return: GroupSlot<Tariff | null>
}

/**
 * Place choisie par direction ET par sous-groupe. Un dossier > 4 passagers est
 * scindé par le backend en group 1 (4 premiers) + group 2 (reste) ; l'user
 * choisit une place pour chaque groupe. Pour ≤ 4 pax, seul `group1` est utilisé.
 */
export interface SelectedPlaces {
  outward: GroupSlot<Place | null>
  return: GroupSlot<Place | null>
}

/** ID du sailing choisi par l'user dans la liste des Results (un sailing = un
 *  horaire donné d'un journey, partagé entre plusieurs availabilities qui ne
 *  diffèrent que par le type de place vendu). */
export interface SelectedSailing {
  outward: string | null
  return: string | null
}

/** Type de tarif (ticket / presale / option) choisi par direction. */
export interface SelectedTariffType {
  outward: string
  return: string
}

export interface WidgetError {
  code: string
  message: string
}

export interface WidgetStore {
  search: SearchState
  availabilities: AvailabilitiesByDirection
  selectedTariffs: SelectedTariffs
  selectedTariffType: SelectedTariffType
  selectedPlaces: SelectedPlaces
  selectedSailing: SelectedSailing
  booking: Booking | null
  state: BookingState
  currentStep: BookingStep
  loading: boolean
  /** Pre-loaded list of all journeys (catalogue) — populated at widget mount. */
  journeyCatalog: Journey[]
  /** Pre-loaded list of vehicle brands — populated at widget mount. */
  brandCatalog: Brand[]
  /** Long-lived cache of vehicle models keyed by brandId — fills lazily. */
  modelsByBrand: Record<string, VehicleModel[]>
  /** True while fetching the full journey variants (post-pick, background). */
  loadingJourney: boolean
  error: WidgetError | null
  /**
   * Mode d'usage du widget — dérivé du token (`m` dans la metadata).
   *   - `'b2c'` : end-user public, redirect vers la page checkout de l'org (origine + /checkout) en fin de funnel
   *   - `'agent'` : back-office d'agent, redirect vers la confirmation SaaS FerryGate
   * Fallback `'agent'` pour les tokens legacy sans `m` (comportement historique).
   */
  mode: WidgetMode
  /** URL absolue de la page checkout de l'org (mode B2C uniquement). */
  checkoutUrl: string | null
  /** Base URL du SaaS FerryGate pour la redirection mode Agent. */
  saasBaseUrl: string | null
  /**
   * URL d'enregistrement du dossier côté beintravel (back-office hôte). Si
   * définie, le submit sauve le booking FerryGate ICI après sa création. Vide/
   * null → étape bein ignorée (hôtes non-beintravel inchangés).
   */
  beintravelUrl: string | null
  /**
   * Id du dossier BeinTravel renvoyé par actionSave (après création). C'est lui
   * qui sert à la redirection vers le checkout de l'hôte (`?booking_id=`).
   */
  beinBookingId: string | null
}

export const STORE_KEY: InjectionKey<WidgetStore> = Symbol('fg-widget-store')

export function createStore(): WidgetStore {
  return reactive<WidgetStore>({
    search: createDefaultSearchState(),
    availabilities: emptyAvailabilities(),
    selectedTariffs: emptySelectedTariffs(),
    selectedTariffType: { outward: 'ticket', return: 'ticket' },
    selectedPlaces: emptySelectedPlaces(),
    selectedSailing: { outward: null, return: null },
    booking: null,
    state: BookingState.Searching,
    currentStep: BookingStep.Search,
    loading: false,
    journeyCatalog: [],
    brandCatalog: [],
    modelsByBrand: {},
    loadingJourney: false,
    error: null,
    mode: 'agent',
    checkoutUrl: null,
    saasBaseUrl: null,
    beintravelUrl: null,
    beinBookingId: null,
  })
}

export function useStore(): WidgetStore {
  return injectStrict(STORE_KEY, 'WidgetStore')
}

export function resetForNewSearch(store: WidgetStore): void {
  store.search.journeys = []
  store.search.request = {}
  store.availabilities = emptyAvailabilities()
  store.selectedTariffs = emptySelectedTariffs()
  store.selectedTariffType = { outward: 'ticket', return: 'ticket' }
  store.selectedPlaces = emptySelectedPlaces()
  store.selectedSailing = { outward: null, return: null }
  store.booking = null
  store.state = BookingState.Searching
  store.currentStep = BookingStep.Search
  store.error = null
}

function createDefaultSearchState(): SearchState {
  return {
    id: Date.now(),
    sessionId: generateSessionId(),
    direction: Direction.Outward,
    request: {},
    journeys: [],
    passengers: [{ order: 1, type: 'Adult', age: 25, param: 'A', gender: 'Male' }],
    vehicle: { type: '', brand: '', model: '', code: '', identification: null },
    pets: { cats: 0, dogs: 0, dogs30: 0 },
    promo: { active: false, name: '', code: '', value: 0 },
    insurance: { active: false, amount: 0, type: 'fixed' },
    ancv: { active: false, amount: 0 },
    contact: { name: '', email: '', prefix: '', phone: '', address: '' },
    ticket_by_email: false,
  }
}

function emptyAvailabilities(): AvailabilitiesByDirection {
  return {
    outward: { primary: null },
    return: {},
  }
}

function emptySelectedTariffs(): SelectedTariffs {
  return {
    outward: { group1: null, group2: null },
    return: { group1: null, group2: null },
  }
}

function emptySelectedPlaces(): SelectedPlaces {
  return {
    outward: { group1: null, group2: null },
    return: { group1: null, group2: null },
  }
}
